import './ResizeHandle.css';

class ResizeHandle {
  constructor(container, targetElement, options = {}) {
    this.container = container;
    this.targetElement = targetElement;
    this.options = Object.assign({
      direction: 'horizontal', // or 'vertical'
      minSize: 100,
      maxSize: 600,
      initialSize: 250,
      storageName: null, // localStorage key to save size
    }, options);
    
    this.isResizing = false;
    this.startPosition = 0;
    this.startSize = 0;
    this.lastAppliedSize = this.options.initialSize; // Initialize with initialSize
    
    // Bind methods
    this.handleMouseDown = this.handleMouseDown.bind(this);
    this.handleMouseMove = this.handleMouseMove.bind(this);
    this.handleMouseUp = this.handleMouseUp.bind(this);
    this.handleTouchStart = this.handleTouchStart.bind(this);
    this.handleTouchMove = this.handleTouchMove.bind(this);
    this.handleTouchEnd = this.handleTouchEnd.bind(this);
  }
  
  /**
   * Render the resize handle component
   */
  render() {
    // Add resize CSS class to the container
    this.container.classList.add('resize-handle');
    
    if (this.options.direction === 'horizontal') {
      this.container.classList.add('resize-handle--horizontal');
    } else {
      this.container.classList.add('resize-handle--vertical');
    }
    
    // Set initial size from localStorage if available
    if (this.options.storageName && window.localStorage) {
      const savedSize = localStorage.getItem(this.options.storageName);
      if (savedSize) {
        this.setTargetSize(parseInt(savedSize, 10));
      } else {
        this.setTargetSize(this.options.initialSize);
      }
    } else {
      this.setTargetSize(this.options.initialSize);
    }
    
    // Set up event listeners
    this.setupEventListeners();
    
    return this;
  }
  
  /**
   * Set up event listeners for resizing
   */
  setupEventListeners() {
    // Mouse events
    this.container.addEventListener('mousedown', this.handleMouseDown);
    
    // Touch events
    this.container.addEventListener('touchstart', this.handleTouchStart, { passive: false });
    
    // Set cursor style
    this.container.style.cursor = this.options.direction === 'horizontal' ? 'ew-resize' : 'ns-resize';
  }
  
  /**
   * Handle mouse down event
   */
  handleMouseDown(e) {
    this.startResizing(e.clientX, e.clientY);
    
    // Add global event listeners
    document.addEventListener('mousemove', this.handleMouseMove);
    document.addEventListener('mouseup', this.handleMouseUp);
    
    e.preventDefault();
  }
  
  /**
   * Handle mouse move event
   */
  handleMouseMove(e) {
    if (!this.isResizing) return;
    
    const position = this.options.direction === 'horizontal' ? e.clientX : e.clientY;
    this.resize(position);
    
    e.preventDefault();
  }
  
  /**
   * Handle mouse up event
   */
  handleMouseUp(e) {
    this.stopResizing();
    
    // Remove global event listeners
    document.removeEventListener('mousemove', this.handleMouseMove);
    document.removeEventListener('mouseup', this.handleMouseUp);
    
    e.preventDefault();
  }
  
  /**
   * Handle touch start event
   */
  handleTouchStart(e) {
    const touch = e.touches[0];
    this.startResizing(touch.clientX, touch.clientY);
    
    // Add global event listeners
    document.addEventListener('touchmove', this.handleTouchMove, { passive: false });
    document.addEventListener('touchend', this.handleTouchEnd);
    document.addEventListener('touchcancel', this.handleTouchEnd);
    
    e.preventDefault();
  }
  
  /**
   * Handle touch move event
   */
  handleTouchMove(e) {
    if (!this.isResizing) return;
    
    const touch = e.touches[0];
    const position = this.options.direction === 'horizontal' ? touch.clientX : touch.clientY;
    this.resize(position);
    
    e.preventDefault();
  }
  
  /**
   * Handle touch end event
   */
  handleTouchEnd(e) {
    this.stopResizing();
    
    // Remove global event listeners
    document.removeEventListener('touchmove', this.handleTouchMove);
    document.removeEventListener('touchend', this.handleTouchEnd);
    document.removeEventListener('touchcancel', this.handleTouchEnd);
    
    e.preventDefault();
  }
  
  /**
   * Start the resizing operation
   */
  startResizing(clientX, clientY) {
    this.isResizing = true;
    this.startPosition = this.options.direction === 'horizontal' ? clientX : clientY;
    
    // Get current size
    const rect = this.targetElement.getBoundingClientRect();
    this.startSize = this.options.direction === 'horizontal' ? rect.width : rect.height;
    
    // Add active class
    this.container.classList.add('resize-handle--active');
    
    // Add a class to the body to disable text selection during resize
    document.body.classList.add('resize-in-progress');
    
    // Add direction-specific class to body for cursor
    if (this.options.direction === 'horizontal') {
      document.body.classList.add('resize-horizontal-in-progress');
    } else {
      document.body.classList.add('resize-vertical-in-progress');
    }
    
    // Dispatch resize start event
    const startEvent = new CustomEvent('resize:start', { 
      detail: { 
        direction: this.options.direction 
      },
      bubbles: true 
    });
    document.dispatchEvent(startEvent);
  }
  
  /**
   * Perform the resizing based on current position
   */
  resize(currentPosition) {
    if (!this.isResizing) return;
    
    const delta = currentPosition - this.startPosition;
    let newSize = this.startSize;
    
    if (this.options.direction === 'horizontal') {
      newSize += delta;
    } else {
      // For vertical resize of terminal, we want to flip the direction
      // Dragging down increases size, dragging up decreases size
      newSize -= delta; 
    }
    
    // Enforce min/max constraints
    newSize = Math.max(this.options.minSize, Math.min(this.options.maxSize, newSize));
    
    this.setTargetSize(newSize);
  }
  
  /**
   * Stop the resizing operation
   */
  stopResizing() {
    if (!this.isResizing) return;
    
    this.isResizing = false;
    
    // Remove active class
    this.container.classList.remove('resize-handle--active');
    
    // Remove body classes
    document.body.classList.remove('resize-in-progress');
    document.body.classList.remove('resize-horizontal-in-progress');
    document.body.classList.remove('resize-vertical-in-progress');
    
    // Save to localStorage if enabled, using the last successfully applied size
    if (this.options.storageName && window.localStorage) {
      localStorage.setItem(this.options.storageName, this.lastAppliedSize.toString());
    }
    
    // Dispatch resize end event
    const endEvent = new CustomEvent('resize:end', { 
      detail: { 
        direction: this.options.direction 
      },
      bubbles: true 
    });
    document.dispatchEvent(endEvent);
  }
  
  /**
   * Set the target element size
   */
  setTargetSize(size) {
    if (this.options.direction === 'horizontal') {
      this.targetElement.style.width = `${size}px`;
    } else {
      this.targetElement.style.height = `${size}px`;
    }
    this.lastAppliedSize = size; // Store the size that was just applied
    
    // Dispatch resize event
    const event = new CustomEvent('resize:complete', { 
      detail: { 
        direction: this.options.direction, 
        size 
      },
      bubbles: true 
    });
    this.container.dispatchEvent(event);
  }
}

export default ResizeHandle; 